import React, { useState } from 'react';
import Badge from '../ui/Badge';
import SafeIcon from '../../common/SafeIcon';
import NewInvoiceModal from '../modals/NewInvoiceModal';

export default function Invoices({ invoices, setInvoices }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const handleSaveInvoice = (newInvoice) => {
    setInvoices([newInvoice, ...invoices]);
  };

  const getStatusVariant = (status) => {
    switch(status.toLowerCase()) {
      case 'paid': return 'success';
      case 'pending': return 'warning';
      case 'overdue': return 'danger';
      default: return 'default';
    }
  };

  const filteredInvoices = invoices.filter(inv => 
    inv.client.toLowerCase().includes(searchTerm.toLowerCase()) || 
    inv.id.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="animate-in fade-in duration-500 h-full flex flex-col">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-light text-cream">Invoice Tracker</h1>
          <p className="text-sm text-cream-muted mt-1">Manage billing and track outstanding payments.</p>
        </div>
        
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative flex-1 sm:w-64">
            <SafeIcon name="Search" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cream-muted" />
            <input 
              type="text" 
              placeholder="Search invoices..." 
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full bg-charcoal-light border border-[#333] rounded-lg pl-9 pr-4 py-2 text-sm text-cream focus:outline-none focus:border-invoice-green transition-colors"
            />
          </div>
          <button 
            onClick={() => setIsModalOpen(true)}
            className="bg-invoice-green hover:bg-emerald-400 text-[#111] px-4 py-2 rounded-lg font-medium text-sm transition-colors flex items-center gap-2 whitespace-nowrap"
          >
            <SafeIcon name="Plus" className="w-4 h-4" />
            New Invoice
          </button>
        </div>
      </div>

      <div className="glass-panel rounded-xl overflow-hidden flex-1 flex flex-col">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="border-b border-[#333] bg-charcoal-light/50">
                <th className="p-4 text-xs font-medium text-cream-muted uppercase tracking-wider">Invoice ID</th>
                <th className="p-4 text-xs font-medium text-cream-muted uppercase tracking-wider">Client & Project</th>
                <th className="p-4 text-xs font-medium text-cream-muted uppercase tracking-wider">Date</th>
                <th className="p-4 text-xs font-medium text-cream-muted uppercase tracking-wider">Amount</th>
                <th className="p-4 text-xs font-medium text-cream-muted uppercase tracking-wider">Status</th>
                <th className="p-4 text-xs font-medium text-cream-muted uppercase tracking-wider text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#222]">
              {filteredInvoices.map((invoice) => (
                <tr key={invoice.id} className="hover:bg-charcoal-light/30 transition-colors group">
                  <td className="p-4 font-mono text-sm text-cream">{invoice.id}</td>
                  <td className="p-4">
                    <p className="text-sm font-medium text-cream">{invoice.client}</p>
                    <p className="text-xs text-cream-muted mt-0.5">{invoice.project}</p>
                  </td>
                  <td className="p-4 text-sm text-cream-muted">{new Date(invoice.date).toLocaleDateString()}</td>
                  <td className="p-4 font-mono text-sm text-cream">${invoice.amount.toLocaleString()}</td>
                  <td className="p-4">
                    <Badge variant={getStatusVariant(invoice.status)}>{invoice.status}</Badge>
                  </td>
                  <td className="p-4 text-right">
                    <button className="text-cream-muted hover:text-invoice-green transition-colors p-2 opacity-0 group-hover:opacity-100">
                      <SafeIcon name="Download" className="w-4 h-4" />
                    </button>
                    <button className="text-cream-muted hover:text-cream transition-colors p-2 opacity-0 group-hover:opacity-100">
                      <SafeIcon name="MoreVertical" className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
              {filteredInvoices.length === 0 && (
                <tr>
                  <td colSpan="6" className="p-8 text-center text-cream-muted">
                    No invoices found matching your criteria.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <NewInvoiceModal 
        isOpen={isModalOpen} 
        onClose={() => setIsModalOpen(false)} 
        onSave={handleSaveInvoice}
      />
    </div>
  );
}